/*
 * File: ProductsInfoSlice.ts
 * Project: receiving-rules
 * -----
 * Last Modified: 30 Sep 2020 5:38:56 pm
 * -----
 * Copyright (c) 2020 Elegant Software Solutions All rights reserved.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/dist/query';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { SalesOrderLineItemModel } from 'models/SalesOrderLineItemModel';
import { SalesOrderLinesPayloadModel } from 'models/SalesOrderPayloadModel';

/* ****************** Interfaces ****************** */

interface ProductsInfoStateModel {
  lineItemsSubmitErrors: QueryReturnValue<SalesOrderLineItemModel, FetchBaseQueryError, FetchBaseQueryMeta>[] | null;
  filterProductName: string | undefined;
  filterProductNumber: string | undefined;
  filterManufacturer: string | undefined;
  filterCategory: string | undefined;
  offset: number;
  selectedLineItemIds: string[];
  selectedProducts: SalesOrderLineItemModel[];
  products: SalesOrderLinesPayloadModel[];
  isChild: boolean;
  duplicateProducts: SalesOrderLineItemModel[];
  isTableLoading: boolean;
  siteId: string;
  ocdValues: string;
}

const initialState: ProductsInfoStateModel = {
  lineItemsSubmitErrors: null,
  filterProductName: '',
  filterProductNumber: '',
  filterManufacturer: '',
  filterCategory: '',
  selectedLineItemIds: [],
  offset: 0,
  selectedProducts: [],
  products: [],
  isChild: false,
  duplicateProducts: [],
  isTableLoading: false,
  siteId: '',
  ocdValues: ''
};

/* ****************** Slice ****************** */
export const productsInfo = createSlice({
  name: 'productsInfo',
  initialState,
  reducers: {
    setFilterProductNumber(state, action: PayloadAction<string>) {
      state.filterProductNumber = action.payload;
    },
    setFilterProductName(state, action: PayloadAction<string>) {
      state.filterProductName = action.payload;
    },
    setFilterManufacturer(state, action: PayloadAction<string>) {
      state.filterManufacturer = action.payload;
    },
    setFilterCategory(state, action: PayloadAction<string>) {
      state.filterCategory = action.payload;
    },
    setLineItemSubmitErrors(state, action) {
      state.lineItemsSubmitErrors = action.payload;
    },
    deselectLineItem: (state, { payload }: PayloadAction<string>) => {
      state.selectedLineItemIds = state.selectedLineItemIds.filter((id) => id !== payload);
    },
    selectLineItem: (state, { payload }: PayloadAction<string>) => {
      state.selectedLineItemIds = [...state.selectedLineItemIds, payload];
    },
    setSelectedLineItems: (state, { payload }: PayloadAction<string[]>) => {
      state.selectedLineItemIds = payload;
    },
    setOffset: (state, { payload }: PayloadAction<number>) => {
      state.offset = payload;
    },
    setSelectedProducts: (state, { payload }: PayloadAction<SalesOrderLineItemModel[]>) => {
      state.selectedProducts = payload;
    },
    addProduct: (state, { payload }: PayloadAction<SalesOrderLineItemModel>) => {
      state.products = [...state.products, payload];
    },
    setProducts: (state, { payload }: PayloadAction<SalesOrderLinesPayloadModel[] | undefined>) => {
      if (!payload) return;
      state.products = payload;
    },
    setDuplicateProducts: (state, { payload }: PayloadAction<SalesOrderLineItemModel[] | undefined>) => {
      if (!payload) return;
      state.products = payload;
    },
    setIsChild: (state, { payload }: PayloadAction<boolean>) => {
      state.isChild = payload;
    },
    setIsTableLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.isTableLoading = payload;
    },
    setSiteId: (state, { payload }: PayloadAction<string>) => {
      state.siteId = payload;
    },
    setOcdValues: (state, { payload }: PayloadAction<string>) => {
      state.ocdValues = payload;
    }
  }
});

export const {
  setFilterProductName,
  setFilterManufacturer,
  setFilterCategory,
  setFilterProductNumber,
  setLineItemSubmitErrors,
  selectLineItem,
  setSelectedLineItems,
  deselectLineItem,
  setOffset,
  setSelectedProducts,
  addProduct,
  setProducts,
  setIsChild,
  setDuplicateProducts,
  setIsTableLoading,
  setOcdValues,
  setSiteId
} = productsInfo.actions;

export const lineItemsReducer = productsInfo.reducer;
